import type { CashflowTarget as ProposalCashflowTarget } from '@moment/horizon-data-access/proposal'
import type { CashflowBucket } from '@/portfolio/proposals/cashflow-target/types'
import type { CashflowTarget } from '@/types/api/accounts'
import type { Cashflow } from '@/types/statistics'

import { Small, TextColor } from '@moment/design-system-ui/text'
import { IsoDateStr } from '@moment/domain-types'
import { todayIsoDate } from '@moment/utils/datetime'
import { format } from 'date-fns/format'
import { parseISO } from 'date-fns/parseISO'

import { MultipleBarChart } from '@/components/atomic/bar-chart/multiple'
import { ChartTooltip } from '@/components/atomic/chart-tooltip'
import { GAP_CASH_COLOR } from '@/portfolio/proposals/cashflow-target/gap-months'
import { PLANNING_TIME_ZONE } from '@/portfolio/proposals/cashflow-target/merge-recurring-targets'
import {
  CashflowChartContainer,
  CashflowChartPatterns,
  CashflowChartTitleContainer,
  CashflowTargetFailureIcon,
  CashflowTargetStatusText,
  CashflowTargetSuccessIcon,
  ChartTitle,
} from '@/portfolio/proposals/cashflow-target/styles'
import {
  formatCashflowData,
  formatCustomData,
  formatPlannedChart,
  getCashflowStatusMessage,
  isCashflowTargetSuccessful,
} from '@/portfolio/proposals/cashflow-target/utils'
import { DateFormat } from '@/utils/dates/dateFormatting'
import {
  formatLargeNumbers,
  formatMoney,
} from '@/utils/presentation/formatting'

// This view's targets come from the accounts API, not the proposal API that
// the stacking planner works with; convert at this boundary, or null on a bad date.
function toProposalCashflowTarget(
  target: CashflowTarget,
): ProposalCashflowTarget | null {
  const endDate = IsoDateStr.safeParse(target.end_date)
  if (!endDate.success) return null
  if (!target.start_date) {
    return { ...target, start_date: undefined, end_date: endDate.data }
  }
  const startDate = IsoDateStr.safeParse(target.start_date)
  if (!startDate.success) return null
  return { ...target, start_date: startDate.data, end_date: endDate.data }
}

function plannedChart(incomeData: Cashflow[], targets: CashflowTarget[]) {
  const proposalTargets: ProposalCashflowTarget[] = []
  for (const target of targets) {
    const converted = toProposalCashflowTarget(target)
    if (!converted) return null
    proposalTargets.push(converted)
  }
  return formatPlannedChart(incomeData, proposalTargets, {
    dt: todayIsoDate(PLANNING_TIME_ZONE),
  })
}

const TooltipFormatter = (dataPoint: CashflowBucket, key: string) => {
  const formatEnd = format(
    parseISO(dataPoint.end_date),
    DateFormat.MONTH_ABBREV_YEAR_ABBREV,
  )
  const formatStart = dataPoint.start_date
    ? format(
        parseISO(dataPoint.start_date),
        DateFormat.MONTH_ABBREV_YEAR_ABBREV,
      )
    : null
  const dateRange = formatStart
    ? `${formatStart} to ${formatEnd}`
    : `Until ${formatEnd}`

  const actualAmount = (dataPoint[key as keyof CashflowBucket] as number) || 0

  if (dataPoint.isGap) {
    if (actualAmount <= 0) return null
    return (
      <ChartTooltip
        className="w-64"
        date={dateRange}
        values={[
          {
            label: 'Cash flow',
            value: formatMoney(actualAmount),
            color: GAP_CASH_COLOR,
          },
        ]}
        footer={
          <Small color={TextColor.Secondary}>
            Outside the target window, so it doesn't count toward a target.
          </Small>
        }
      />
    )
  }

  const targetAmount = dataPoint.amount

  const isSuccessful = isCashflowTargetSuccessful(actualAmount, targetAmount)
  const message = getCashflowStatusMessage(actualAmount, targetAmount)

  const headerIcon = isSuccessful ? (
    <CashflowTargetSuccessIcon />
  ) : (
    <CashflowTargetFailureIcon />
  )

  const footerText = (
    <CashflowTargetStatusText isSuccessful={isSuccessful} message={message} />
  )

  const values = [
    {
      label: 'Cash flow target',
      value: formatMoney(targetAmount),
      color: 'var(--color-moment-blue-400)',
    },
    {
      label: 'Actual cash flow',
      value: formatMoney(actualAmount),
      color:
        actualAmount < targetAmount
          ? 'var(--color-moment-red-700)'
          : 'var(--color-bar-moment-chart-forest-full)',
    },
  ]

  return (
    <ChartTooltip
      className="w-64"
      date={dateRange}
      values={values}
      headerIcon={headerIcon}
      footer={footerText}
    />
  )
}

const YAxisFormatter = (value: number) => {
  return `$${formatLargeNumbers(value.toString())}`
}

export const CashflowIncomeChart = ({
  incomeData,
  cashflowTargets,
}: {
  incomeData: Cashflow[]
  cashflowTargets?: CashflowTarget[]
}) => {
  // 2+ recurring targets render as the buckets the backend graded today.
  const planned = cashflowTargets
    ? plannedChart(incomeData, cashflowTargets)
    : null
  // Determine which data format to use
  const useCustomData = cashflowTargets && cashflowTargets.length > 1
  // biome-ignore lint/complexity/useOptionalChain: Bulk suppression as part of biome migration
  const targetData = cashflowTargets && cashflowTargets[0]
  const unplannedData = useCustomData
    ? formatCustomData(incomeData, cashflowTargets)
    : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
      targetData
      ? formatCashflowData(incomeData, targetData)
      : []
  const data = planned?.data ?? unplannedData

  // Determine the key based on the source
  // For custom data with multiple targets, use total_amount as default
  // For single target, use the specific source
  const unplannedKey = useCustomData
    ? 'total_amount'
    : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
      targetData?.source === 'coupon_and_principal'
      ? 'total_amount'
      : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
        targetData?.source === 'coupon'
        ? 'coupon_amount'
        : 'principal_amount'
  const key = planned?.key ?? unplannedKey

  // Transform data to include conditional fields for rendering
  const chartData = data.map((bucket) => {
    const actualAmount = (bucket[key as keyof CashflowBucket] as number) || 0
    if (bucket.isGap) {
      return {
        ...bucket,
        actual_compliant: 0,
        actual_violation: 0,
        shortfall: 0,
        gap_cash: actualAmount,
      }
    }
    const targetAmount = bucket.amount
    const shortfall = Math.max(0, targetAmount - actualAmount)

    return {
      ...bucket,
      actual_compliant: shortfall > 0 ? 0 : actualAmount,
      actual_violation: shortfall > 0 ? actualAmount : 0,
      shortfall: shortfall,
    }
  })

  return (
    <CashflowChartContainer className="w-full">
      <CashflowChartTitleContainer>
        <ChartTitle>Cash Flow</ChartTitle>
      </CashflowChartTitleContainer>
      <div className="h-[300px]">
        <MultipleBarChart
          data={chartData}
          barSize={200}
          xAxisKey="start_date_formatted"
          bars={[
            {
              key: 'actual_compliant',
              dataKey: 'actual_compliant',
              fill: 'var(--color-bar-moment-chart-forest-full)',
              stackId: 'cashflow',
              radius: [4, 4, 0, 0],
            },
            {
              key: 'actual_violation',
              dataKey: 'actual_violation',
              fill: 'var(--color-moment-red-700)',
              stackId: 'cashflow',
              radius: [4, 4, 0, 0],
            },
            {
              key: 'shortfall',
              dataKey: 'shortfall',
              fill: 'url(#diagonalHatch)',
              stackId: 'cashflow',
              radius: [4, 4, 0, 0],
            },
            {
              key: 'gap_cash',
              dataKey: 'gap_cash',
              fill: GAP_CASH_COLOR,
              stackId: 'cashflow',
              radius: [4, 4, 0, 0],
            },
          ]}
          tooltipFormatter={(dataPoint: CashflowBucket) =>
            TooltipFormatter(dataPoint, key)
          }
          hideYAxis={false}
          yAxisFormatter={YAxisFormatter}
        />
        <CashflowChartPatterns />
      </div>
    </CashflowChartContainer>
  )
}
